import React from 'react';
import { Button } from '@ygtechnologies/rubus-ui';
import { Trans } from '@lingui/react/macro';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheck, faTriangleExclamation } from '@fortawesome/pro-light-svg-icons';
import { ConfiguratorGroupDto, ConfiguratorOptionDto } from '@/services/configurator/configurator.types';
import { Banner } from '@ygtechnologies/bygr-frontend-component-library';
import { BuyerOptionCard } from '../BuyerOptionCard/BuyerOptionCard';
import theme from './DependencyWizardModal.module.scss';

interface DependencyWizardStepProps {
    group: ConfiguratorGroupDto;
    triggerGroupName: string;
    triggerOptionName: string;
    knownSelections: Record<number, ConfiguratorOptionDto>;
    pendingOption: ConfiguratorOptionDto | null;
    onSelect: (option: ConfiguratorOptionDto) => void;
}

export const DependencyWizardStep: React.FC<DependencyWizardStepProps> = ({
    group,
    triggerGroupName,
    triggerOptionName,
    knownSelections,
    pendingOption,
    onSelect,
}) => {
    // Each option knows its own dependency conflicts. An option is incompatible if any of its
    // dependencyConflicts entries matches a known selection in that related group.
    const hasConstraints = group.options.some((o) =>
        o.dependencyConflicts.some((fw) => knownSelections[fw.groupId] !== undefined)
    );
    const allowedOptionIds: Set<number> | null = hasConstraints
        ? new Set(
              group.options
                  .filter((o) => !o.dependencyConflicts.some((fw) => knownSelections[fw.groupId]?.id === fw.optionId))
                  .map((o) => o.id)
          )
        : null;

    const pendingOptionIsIncompatible =
        pendingOption !== null && allowedOptionIds !== null && !allowedOptionIds.has(pendingOption.id);

    const renderSelectButton = (option: ConfiguratorOptionDto) => {
        const isSelected = pendingOption?.id === option.id;
        const isIncompatible = allowedOptionIds !== null && !allowedOptionIds.has(option.id);

        if (isSelected && isIncompatible) {
            return (
                <span className={theme.selectedIncompatible}>
                    <FontAwesomeIcon icon={faTriangleExclamation} />
                    <Trans>Invalid selection</Trans>
                </span>
            );
        }

        if (isSelected) {
            return (
                <span className={theme.selectedIndicator}>
                    <FontAwesomeIcon icon={faCheck} />
                    <Trans>Selected</Trans>
                </span>
            );
        }

        return (
            <Button variant="tertiary" size="sm" onClick={() => onSelect(option)} disabled={isIncompatible}>
                <Trans>Select</Trans>
            </Button>
        );
    };

    return (
        <div className={theme.step}>
            <Banner type={pendingOptionIsIncompatible ? 'danger' : 'warning'}>
                <Trans>
                    Your selection of <strong>{triggerOptionName}</strong> in <strong>{triggerGroupName}</strong> affects
                    this option group. Please review your selection below.
                </Trans>
            </Banner>

            {group.description && <p className={theme.groupDescription}>{group.description}</p>}

            <div className={theme.optionList}>
                {group.options.map((option) => (
                    <BuyerOptionCard
                        key={option.id}
                        option={option}
                        renderSelectButton={renderSelectButton}
                    />
                ))}
            </div>
        </div>
    );
};
